import { CandidateRegistrationDtoClient } from "@/api/candidate/dto/candidate-registration.dto";
import { ValidationStatus } from "@/api/enums/ValidationStatus";
import { keyFactory } from "@/api/keyFactory";
import { useMutationRegistrationPreTestStatus } from "@/api/registration/registration";
import { Switch } from "@/components/atoms/Switch";
import PreTestStatusBadge from "@/components/molecules/Badges/PreTestStatusBadge";
import ConfirmDialog from "@/components/molecules/ConfirmDialog";
import { Card, CardTitle } from "@/components/molecules/RegistrationCard/Card";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";

interface PreTestCardProps {
  registration: CandidateRegistrationDtoClient;
  candidateId: number;
}

const PreTestCard = ({
  registration: {
    preTestStatus,
    id: registrationId,
    exam: { registeringEndDate },
  },
  candidateId: id,
}: PreTestCardProps) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const isValidated = preTestStatus === ValidationStatus.VALIDATED;
  const queryClient = useQueryClient();

  const statusMutation = useMutationRegistrationPreTestStatus();

  const isExamRegistrationClosed = useMemo(() => {
    return new Date(registeringEndDate) < new Date();
  }, [registeringEndDate]);

  return (
    <>
      <ConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        title={(isValidated ? "Invalider" : "Valider") + " le test préalable"}
        cancelStr="Annuler"
        validateStr="Confirmer"
        onResult={(accepted) => {
          if (accepted) {
            statusMutation.mutate(
              {
                registrationId,
                validated: !isValidated,
              },
              {
                onSuccess: () => {
                  toast.success(
                    "Test préalable " + (isValidated ? "invalidé" : "validé")
                  );

                  queryClient.invalidateQueries({
                    queryKey: keyFactory.candidate.onGoingRegistration(id),
                  });

                  queryClient.invalidateQueries({
                    queryKey: keyFactory.exam.base(),
                  });

                  queryClient.invalidateQueries({
                    queryKey: keyFactory.registration.base(),
                  });
                },
                onError: () => {
                  toast.error("Échec de la requête", {
                    description:
                      "Votre action n'a pas pu être prise en compte.",
                  });
                },
              }
            );
          }
          setConfirmDialogOpen(false);
        }}
      />
      <Card>
        <div className="flex flex-row justify-between w-full items-center">
          <div className="flex flex-row gap-2">
            <CardTitle>Test préalable</CardTitle>
            <PreTestStatusBadge
              status={preTestStatus}
              isExamRegistrationClosed={isExamRegistrationClosed}
            />
          </div>
          <Switch
            checked={isValidated}
            className="data-[state=checked]:bg-green-600"
            onCheckedChange={() => setConfirmDialogOpen(true)}
          />
        </div>
      </Card>
    </>
  );
};

export default PreTestCard;
